import { PlatformPricingTier, Tenant, TenantDashboard, UserRole } from '@domain/graphql.types';
import { paidPlatformPricingTiers } from '@domain/billing/paying.tiers';
import { BarChartOutlined, MapOutlined, SettingsOutlined } from '@mui/icons-material';
import type { ReactElement } from 'react';
import { getSiteLookupDashboardRoute, Routes, siteLookupDashboardPrefix } from '@application/routes/routes';

export type MenuItem = {
  id: string;
  children?: MenuItem[];
  translationKey?: string;
  label?: string;
  icon?: ReactElement;
  to?: string;
  roles?: UserRole[];
  pricingTiers?: PlatformPricingTier[];
  activeFor?: string[];
  activePrefixes?: string[];
};

const getMenuItems = (tenant?: Tenant): MenuItem[] => {
  const dynamicDashboards: MenuItem[] =
    [...(tenant?.dashboards || [])]
      .sort((a: TenantDashboard, b: TenantDashboard) => a.position - b.position || a.title.localeCompare(b.title))
      .map((dashboard) => ({
        id: `dashboard-${dashboard.id}`,
        label: dashboard.title,
        to: getSiteLookupDashboardRoute(dashboard.key),
      })) || [];

  const dashboardsCategory: MenuItem[] =
    dynamicDashboards.length > 0
      ? [
          {
            id: 'dashboards',
            translationKey: 'navigation.dashboard',
            icon: <BarChartOutlined fontSize="small" />,
            activePrefixes: [siteLookupDashboardPrefix],
            children: dynamicDashboards,
          },
        ]
      : [];

  return [
    {
      id: 'site-lookup',
      translationKey: 'navigation.siteLookup',
      icon: <MapOutlined fontSize="small" />,
      to: Routes.SITE_LOOKUP,
      children: [
        {
          id: 'site-lookup-single-address',
          translationKey: 'navigation.singleAddress',
          to: Routes.SITE_LOOKUP,
        },
        {
          id: 'site-lookup-bulk',
          translationKey: 'navigation.bulk',
          to: Routes.BULK_SEARCH,
          pricingTiers: paidPlatformPricingTiers,
        },
        {
          id: 'site-lookup-reports',
          translationKey: 'navigation.reports',
          to: Routes.REPORTS,
        },
      ],
    },
    ...dashboardsCategory,
    {
      id: 'settings',
      translationKey: 'navigation.settings',
      icon: <SettingsOutlined fontSize="small" />,
      to: Routes.TENANT_USERS,
      roles: [UserRole.Admin, UserRole.Owner],
      activeFor: [Routes.TENANT_INVITATIONS, Routes.TENANT_SUBSCRIPTION],
    },
  ];
};

export default getMenuItems;
